import { DialogComponent } from '@theme/dialog';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (!this.serverDisabledMinus) {
      minusButton.disabled = value < min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Handles the quantity decrease event.
   * Cart quantity stops at 1 and opens a confirmation dialog instead of going to 0.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { min, value } = this.getCurrentValues();
    if (value <= min) {
      const removeDialog = document.getElementById('cart-remove-confirmation-dialog');

      if (removeDialog instanceof CartRemoveConfirmationDialog) {
        removeDialog.openForSelector(this);
      }

      return;
    }

    super.decreaseQuantity(event);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}

class CartRemoveConfirmationDialog extends DialogComponent {
  /** @type {CartQuantitySelectorComponent | null} */
  #triggerSelector = null;

  /**
   * Opens the confirmation dialog for a specific cart quantity selector.
   * @param {CartQuantitySelectorComponent} selector
   */
  openForSelector(selector) {
    this.#triggerSelector = selector;
    this.showDialog();

    requestAnimationFrame(() => {
      const confirmButton = this.querySelector('[data-cart-remove-confirm-button]');
      if (confirmButton instanceof HTMLButtonElement) {
        confirmButton.focus();
      }
    });
  }

  /**
   * Confirms removal of the active line item.
   * @param {Event} event
   */
  confirmRemove(event) {
    event.preventDefault();

    const selector = this.#triggerSelector;
    const quantityInput = selector?.querySelector('input[data-cart-line]');
    const cartLine = quantityInput instanceof HTMLInputElement ? Number(quantityInput.dataset.cartLine) : NaN;

    if (selector && Number.isFinite(cartLine)) {
      selector.dispatchEvent(new QuantitySelectorUpdateEvent(0, cartLine));
    }

    this.closeDialog();
  }

  /**
   * Cancels the removal flow and closes the dialog.
   * @param {Event} event
   */
  cancelRemove(event) {
    event.preventDefault();
    this.closeDialog();
  }

  async closeDialog() {
    await super.closeDialog();
    this.#triggerSelector = null;
  }
}

if (!customElements.get('cart-remove-confirmation-dialog')) {
  customElements.define('cart-remove-confirmation-dialog', CartRemoveConfirmationDialog);
}
